import React from "react";
import styled from "styled-components";

/**
 * The photo-plus-metrics section. Shared by the v2 and v3 landers: identical
 * layout, different copy and badge tone. See Hero.styles.ts for the px-not-rem
 * note.
 */
const Wrapper = styled.section<{ $badgeBg: string }>`
  background: #fff;

  .inner {
    margin: 0 auto;
    max-width: 1152px;
    display: grid;
    gap: 40px;
    padding: 80px 20px;
  }

  .inner > * {
    min-width: 0;
  }

  .media {
    position: relative;
    order: 2;
  }

  .frame {
    position: relative;
    overflow: hidden;
    border-radius: 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 30px 60px -30px rgba(14, 17, 22, 0.35);
  }

  .frame img {
    display: block;
    width: 100%;
    aspect-ratio: 4 / 5;
    object-fit: cover;
  }

  @supports not (aspect-ratio: 4 / 5) {
    .frame img {
      height: auto;
    }
  }

  /* darkens the lower third so the caption stays legible over the photo */
  .scrim {
    pointer-events: none;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 33.333%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0));
  }

  .caption {
    position: absolute;
    left: 20px;
    right: 20px;
    bottom: 20px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    color: #fff;
  }

  .caption-kicker {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: rgba(255, 255, 255, 0.7);
  }

  .caption-quote {
    margin: 4px 0 0;
    font-family: var(--brand-display);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
  }

  .badge {
    position: absolute;
    right: -16px;
    bottom: -16px;
    transform: rotate(-4deg);
    border-radius: 16px;
    background: ${(props) => props.$badgeBg};
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3);
  }

  .content {
    order: 1;
  }

  .eyebrow {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: var(--brand-red);
  }

  .heading {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 36px;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.025em;
    color: var(--brand-ink);

    .accent {
      color: var(--brand-red);
    }
  }

  .copy {
    margin: 20px 0 0;
    font-size: 18px;
    line-height: 1.625;
    color: rgba(14, 17, 22, 0.7);
  }

  .metrics {
    margin-top: 32px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    text-align: center;
  }

  .metric {
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: var(--brand-bone);
    padding: 16px;
  }

  .metric-value {
    display: block;
    font-family: var(--brand-display);
    font-size: 24px;
    font-weight: 800;
    color: var(--brand-ink);
  }

  .metric-label {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(14, 17, 22, 0.5);
  }

  /* three metrics across a 320px screen leaves ~85px each */
  @media screen and (max-width: 400px) {
    .metric {
      padding: 12px 6px;
    }
    .metric-value {
      font-size: 20px;
    }
    .metric-label {
      font-size: 10px;
      letter-spacing: 0.04em;
    }
  }

  @media screen and (min-width: 640px) {
    .inner {
      padding: 80px 32px;
    }
    .heading {
      font-size: 48px;
    }
    .metric-value {
      font-size: 30px;
    }
  }

  @media screen and (min-width: 1024px) {
    .inner {
      grid-template-columns: 1.05fr 1fr;
      align-items: center;
      gap: 56px;
      padding: 96px 32px;
    }
    /* photo moves to the left column on wide screens */
    .media {
      order: 1;
    }
    .content {
      order: 2;
    }
  }
`;

export type Metric = { value: string; label: string };

const RealLife = ({
  image,
  alt,
  kicker,
  quote,
  badge,
  metrics,
  eyebrow,
  heading,
  copy,
  badgeBg = "var(--brand-red)",
  className = "section-lp2-reallife",
}: {
  image: string;
  alt: string;
  kicker: string;
  quote: string;
  badge: string;
  metrics: Metric[];
  eyebrow: string;
  heading: React.ReactNode;
  copy: React.ReactNode;
  badgeBg?: string;
  className?: string;
}) => {
  return (
    <Wrapper className={className} $badgeBg={badgeBg}>
      <div className="inner">
        <div className="media">
          <div className="frame">
            <img src={image} alt={alt} loading="lazy" />
            <span className="scrim" aria-hidden="true" />
            <div className="caption">
              <div>
                <span className="caption-kicker">{kicker}</span>
                <p className="caption-quote">{quote}</p>
              </div>
            </div>
          </div>
          <div className="badge">{badge}</div>
        </div>

        <div className="content">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="heading">{heading}</h2>
          <p className="copy">{copy}</p>
          <div className="metrics">
            {metrics.map(({ value, label }) => (
              <div className="metric" key={label}>
                <span className="metric-value">{value}</span>
                <span className="metric-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default RealLife;
